/**
 * DAC-07 navigation interactions (baked-in A/B winner, native rebuild).
 *
 * Operates on the server-rendered markup from snippets/dac-nav.liquid and
 * dac-country-selector.liquid. Unlike the GrowthBook experiment script, this
 * builds NO DOM and holds NO navData — it only wires behavior:
 *   - desktop dropdown / flyout open-close + positioning
 *   - mobile drill-down panel stack (+ reset on drawer close)
 *   - the mobile country selector toggle / submit
 *   - megamenu_click dataLayer analytics (4 fields, matching the theme)
 *
 * Loaded with `defer` from snippets/header-V2.liquid.
 */
(function () {
  "use strict";
  if (window.__dacNavInit) return;
  window.__dacNavInit = true;

  var OPEN_DELAY = 200;
  var CLOSE_DELAY = 150;

  /* ------------------------------- Analytics ------------------------------ */

  function pushMegamenuClick(categoryName, link) {
    var labelEl = link.querySelector(".dac-label");
    var elementName =
      (labelEl && labelEl.textContent.trim()) ||
      link.getAttribute("title") ||
      (link.textContent || "").trim().split("\n")[0];
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "megamenu_click",
      megamenu_category_name: categoryName || "",
      megamenu_element_name: elementName || "",
      megamenu_element_type: link.querySelector("img") ? "image" : "text",
    });
  }

  /* -------------------------------- Desktop ------------------------------- */

  function closeFlyouts(scope, except) {
    scope.querySelectorAll(".dac-nav__subitem.is-open").forEach(function (subitem) {
      if (subitem === except) return;
      var subtrigger = subitem.querySelector(":scope > .dac-nav__subtrigger");
      var flyout = subitem.querySelector(":scope > .dac-nav__flyout");
      if (subtrigger) subtrigger.setAttribute("aria-expanded", "false");
      if (flyout) flyout.hidden = true;
      subitem.classList.remove("is-open");
    });
  }

  function closeAllDropdowns(nav, except) {
    nav.querySelectorAll(".dac-nav__item.is-open").forEach(function (item) {
      if (item === except) return;
      var trigger = item.querySelector(":scope > .dac-nav__trigger");
      var dropdown = item.querySelector(":scope > .dac-nav__dropdown");
      if (trigger) trigger.setAttribute("aria-expanded", "false");
      if (dropdown) dropdown.hidden = true;
      item.classList.remove("is-open");
      closeFlyouts(item);
    });
  }

  function bindDesktopEvents(nav) {
    var closeTimer = null;
    var openTimer = null;
    var clearOpenTimer = function () {
      window.clearTimeout(openTimer);
      openTimer = null;
    };
    var clearCloseTimer = function () {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    };

    var openDropdown = function (item) {
      closeAllDropdowns(nav, item);
      var trigger = item.querySelector(":scope > .dac-nav__trigger");
      var dropdown = item.querySelector(":scope > .dac-nav__dropdown");
      if (!trigger || !dropdown) return;
      trigger.setAttribute("aria-expanded", "true");
      // Full-width and centered dropdowns float below the sticky header.
      if (
        dropdown.classList.contains("dac-nav__dropdown--mega") ||
        dropdown.classList.contains("dac-nav__dropdown--grouped")
      ) {
        var header = document.querySelector("header.header-dac");
        var top = header ? header.getBoundingClientRect().bottom : 0;
        dropdown.style.top = Math.max(top, 0) + "px";
      }
      dropdown.hidden = false;
      item.classList.add("is-open");
    };

    var scheduleOpenDropdown = function (item) {
      clearOpenTimer();
      openTimer = window.setTimeout(function () {
        openTimer = null;
        openDropdown(item);
      }, OPEN_DELAY);
    };

    var closeDropdown = function (item) {
      var trigger = item.querySelector(":scope > .dac-nav__trigger");
      var dropdown = item.querySelector(":scope > .dac-nav__dropdown");
      if (trigger) trigger.setAttribute("aria-expanded", "false");
      if (dropdown) dropdown.hidden = true;
      item.classList.remove("is-open");
      closeFlyouts(item);
    };

    nav.querySelectorAll(".dac-nav__item[data-has-children]").forEach(function (item) {
      item.addEventListener("mouseenter", function () {
        clearCloseTimer();
        scheduleOpenDropdown(item);
      });
      item.addEventListener("mouseleave", function () {
        clearOpenTimer();
        closeTimer = window.setTimeout(function () {
          closeDropdown(item);
        }, CLOSE_DELAY);
      });
      var trigger = item.querySelector(":scope > .dac-nav__trigger");
      if (trigger) {
        trigger.addEventListener("click", function (event) {
          event.preventDefault();
          clearOpenTimer();
          clearCloseTimer();
          if (item.classList.contains("is-open")) {
            closeDropdown(item);
          } else {
            openDropdown(item);
          }
        });
      }
    });

    nav.querySelectorAll(".dac-nav__subitem[data-has-children]").forEach(function (subitem) {
      subitem.addEventListener("mouseenter", function () {
        closeFlyouts(nav, subitem);
        var subtrigger = subitem.querySelector(":scope > .dac-nav__subtrigger");
        var flyout = subitem.querySelector(":scope > .dac-nav__flyout");
        if (subtrigger) subtrigger.setAttribute("aria-expanded", "true");
        if (flyout) {
          flyout.hidden = false;
          // Inside the centered grouped dropdown, render the flyout beside the
          // panel, top-aligned with it, rather than from the triggering row.
          var groupedPanel = subitem.closest(".dac-nav__dropdown--grouped");
          if (groupedPanel) {
            var rect = groupedPanel.getBoundingClientRect();
            flyout.style.position = "fixed";
            flyout.style.top = rect.top + "px";
            flyout.style.left = rect.right + "px";
          }
        }
        subitem.classList.add("is-open");
      });
      subitem.addEventListener("mouseleave", function () {
        var subtrigger = subitem.querySelector(":scope > .dac-nav__subtrigger");
        var flyout = subitem.querySelector(":scope > .dac-nav__flyout");
        if (subtrigger) subtrigger.setAttribute("aria-expanded", "false");
        if (flyout) flyout.hidden = true;
        subitem.classList.remove("is-open");
      });
    });

    // Analytics: any link click inside the desktop nav.
    nav.addEventListener("click", function (event) {
      var link = event.target.closest("a[href]");
      if (!link || !nav.contains(link)) return;
      var openItem = link.closest(".dac-nav__item");
      var catEl = openItem && openItem.querySelector(":scope > .dac-nav__trigger .dac-label");
      pushMegamenuClick(catEl ? catEl.textContent.trim() : "", link);
    });

    document.addEventListener("keydown", function (event) {
      if (event.key !== "Escape") return;
      clearOpenTimer();
      closeAllDropdowns(nav);
    });
    document.addEventListener("click", function (event) {
      if (nav.contains(event.target)) return;
      clearOpenTimer();
      closeAllDropdowns(nav);
    });
  }

  /* -------------------------------- Mobile -------------------------------- */

  function bindMobileEvents(root) {
    var stack = ["root"];

    var showPanel = function (id, direction) {
      root.querySelectorAll(".dac-mnav__panel").forEach(function (panel) {
        var isActive = panel.dataset.panel === id;
        panel.hidden = !isActive;
        panel.classList.toggle("is-active", isActive);
        panel.classList.remove("from-right", "from-left");
        if (isActive) panel.classList.add(direction === "back" ? "from-left" : "from-right");
      });
      root.querySelectorAll(".dac-mnav__trigger[aria-expanded='true']").forEach(function (trigger) {
        if (trigger.dataset.target !== id) trigger.setAttribute("aria-expanded", "false");
      });
    };

    root.addEventListener("click", function (event) {
      var trigger = event.target.closest(".dac-mnav__trigger");
      if (trigger && root.contains(trigger)) {
        event.preventDefault();
        var target = trigger.dataset.target;
        trigger.setAttribute("aria-expanded", "true");
        stack.push(target);
        showPanel(target, "forward");
        return;
      }
      var back = event.target.closest(".dac-mnav__back");
      if (back && root.contains(back)) {
        event.preventDefault();
        stack.pop();
        var backTarget = back.dataset.target || stack[stack.length - 1] || "root";
        showPanel(backTarget, "back");
        return;
      }
      // Analytics + navigation for leaf links.
      var link = event.target.closest("a[href]");
      if (link && root.contains(link)) {
        var panel = link.closest(".dac-mnav__panel");
        var titleEl = panel && panel.querySelector(".dac-mnav__back-title");
        event.preventDefault();
        pushMegamenuClick(titleEl ? titleEl.textContent.trim() : "", link);
        var href = link.href;
        window.setTimeout(function () {
          window.location.href = href;
        }, 200);
      }
    });

    root.__dacResetNav = function () {
      stack.length = 0;
      stack.push("root");
      showPanel("root", "back");
    };
  }

  /**
   * Reset the mobile drill-down to the root panel whenever the drawer closes,
   * so reopening always starts from the top level.
   */
  function watchDrawerClose(root) {
    var drawer = document.getElementById("drawer_menu-mobile_menu");
    if (!drawer) return;
    var isOpen = function () {
      return drawer.className.indexOf("translate-x-0") !== -1;
    };
    var wasOpen = isOpen();
    var observer = new MutationObserver(function () {
      var open = isOpen();
      if (wasOpen && !open && typeof root.__dacResetNav === "function") {
        root.__dacResetNav();
      }
      wasOpen = open;
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ["class"] });
  }

  /* --------------------------- Country selector --------------------------- */

  function bindCountrySelector(form) {
    var toggle = form.querySelector(".dac-country__toggle");
    var list = form.querySelector(".dac-country__list");
    var codeInput = form.querySelector('input[name="country_code"]');
    if (!toggle || !list || !codeInput) return;

    toggle.addEventListener("click", function () {
      var open = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", String(!open));
      list.hidden = open;
    });

    list.addEventListener("click", function (event) {
      var option = event.target.closest(".dac-country__option");
      if (!option) return;
      codeInput.value = option.getAttribute("data-code");
      form.submit();
    });

    document.addEventListener("click", function (event) {
      if (form.contains(event.target)) return;
      toggle.setAttribute("aria-expanded", "false");
      list.hidden = true;
    });
  }

  /* --------------------------------- Init --------------------------------- */

  function init() {
    var nav = document.querySelector(".dac-nav");
    if (nav && !nav.__dacBound) {
      nav.__dacBound = true;
      bindDesktopEvents(nav);
    }

    var mnav = document.querySelector(".dac-mnav");
    if (mnav && !mnav.__dacBound) {
      mnav.__dacBound = true;
      bindMobileEvents(mnav);
      watchDrawerClose(mnav);
    }

    var countryForm = document.querySelector(".dac-country .dac-country__form");
    if (countryForm && !countryForm.__dacBound) {
      countryForm.__dacBound = true;
      bindCountrySelector(countryForm);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
